import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import type { SvgIconComponent } from "@mui/icons-material";
import type { JobEventsController } from "../../hooks/useJobEventController";
import { EJobStatuses } from "../../../../enum/job.enums";
import type { FilterColorScheme, FilterOption } from "./job-event-filters.const";
import {
  BILLING_FILTERS,
  BILLING_SCHEME,
  STATUS_FILTERS,
  STATUS_SCHEME,
  TIME_FILTERS,
  TIME_SCHEME,
} from "./job-event-filters.const";
import type { BillingFilter, JobEventStatusFilter, TimeRangePreset } from "./job-event-filters.types";

/* ═══════════════════════════════════════════════════════════
   FilterChip – single reusable chip for any filter category
   ═══════════════════════════════════════════════════════════ */

function FilterChip({
  label,
  icon: Icon,
  active,
  onClick,
  scheme,
}: {
  label: string;
  icon: SvgIconComponent;
  active: boolean;
  onClick: () => void;
  scheme: FilterColorScheme;
}) {
  return (
    <Button
      onClick={onClick}
      disableRipple
      sx={{
        textTransform: "none",
        height: 30,
        px: 1.5,
        fontWeight: active ? 600 : 400,
        fontSize: 12,
        minWidth: "auto",
        borderRadius: "16px",
        backgroundColor: active ? scheme.activeBg : "transparent",
        border: `1px solid ${active ? scheme.activeBorder : "transparent"}`,
        color: active ? scheme.activeText : "text.secondary",
        transition: "all 120ms ease-in-out",
        "&:hover": {
          backgroundColor: active ? scheme.activeBg : scheme.hoverBg,
          border: `1px solid ${active ? scheme.activeBorder : scheme.hoverBorder}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Icon sx={{ fontSize: 16, color: active ? scheme.activeIcon : "text.disabled", transition: "color 120ms ease" }} />
        {label}
      </Box>
    </Button>
  );
}

/* ═══════════════════════════════════════════════════════════
   FilterGroup – renders a row of FilterChips (single-select)
   ═══════════════════════════════════════════════════════════ */

function FilterGroup<V extends string>({
  options,
  value,
  onChange,
  scheme,
}: {
  options: readonly FilterOption<V>[];
  value: V | undefined;
  onChange: (next: V | undefined) => void;
  scheme: FilterColorScheme;
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {options.map((opt) => (
        <FilterChip
          key={opt.value}
          label={opt.label}
          icon={opt.icon}
          active={value === opt.value}
          onClick={() => onChange(value === opt.value ? undefined : opt.value)}
          scheme={scheme}
        />
      ))}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════
   JobEventFilters – main orchestrator
   ═══════════════════════════════════════════════════════════ */

interface Props {
  controller: JobEventsController;
  resetPage: () => void;
}

export function JobEventFilters({ controller, resetPage }: Props) {
  const handleStatusChange = (val: JobEventStatusFilter | undefined) => {
    controller.setStatus(!val || val === "ALL" ? undefined : (val as EJobStatuses));
    resetPage();
  };

  const handleTimeChange = (val: TimeRangePreset | undefined) => {
    controller.setTimeRange(val);
    if (val !== "CUSTOM") {
      controller.setCustomStartDate(undefined);
      controller.setCustomEndDate(undefined);
    }
    resetPage();
  };

  const handleBillingChange = (val: BillingFilter | undefined) => {
    controller.setBillingStatus(val);
    resetPage();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {/* Row 1: Status + Time + Billing */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 0.25, color: "text.secondary" }}>
          <SearchOutlined sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Filters</Typography>
        </Box>

        <FilterGroup
          options={STATUS_FILTERS}
          value={controller.status ?? "ALL"}
          onChange={handleStatusChange}
          scheme={STATUS_SCHEME}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        <FilterGroup
          options={TIME_FILTERS}
          value={controller.timeRange}
          onChange={handleTimeChange}
          scheme={TIME_SCHEME}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        <FilterGroup
          options={BILLING_FILTERS}
          value={controller.billingStatus}
          onChange={handleBillingChange}
          scheme={BILLING_SCHEME}
        />
      </Box>

      {/* Row 2: Custom date inputs (only when CUSTOM time range is active) */}
      {controller.timeRange === "CUSTOM" && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: 0.5 }}>
          <TextField
            type="date"
            size="small"
            label="From"
            value={controller.customStartDate ?? ""}
            onChange={(e) => {
              controller.setCustomStartDate(e.target.value || undefined);
              resetPage();
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, "& .MuiInputBase-input": { fontSize: 12, py: 0.75 } }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={controller.customEndDate ?? ""}
            onChange={(e) => {
              controller.setCustomEndDate(e.target.value || undefined);
              resetPage();
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, "& .MuiInputBase-input": { fontSize: 12, py: 0.75 } }}
          />
        </Box>
      )}
    </Box>
  );
}
